import React, { useState } from "react";
import {
  Stack,
  TextField,
  Checkbox,
  FormControlLabel,
  Typography,
  CircularProgress,
  Button,
  Snackbar,
  SnackbarContent,
  Autocomplete,
  Dialog,
  DialogActions,
  DialogTitle,
  styled,
  IconButton,
  Tooltip,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import AddIcon from "@mui/icons-material/Add";
import { CloseRounded } from "@mui/icons-material";
import { useStaahRooms } from "../hooks/useStaahRooms";
import { useAuth, PrimaryButton } from "../../sdk";
import { DialogLabel } from "../../BusinessOnBooks/Styles";
import DeleteIcon from "@mui/icons-material/Delete";

const roomTypes = [
  { value: "single", label: "Single" },
  { value: "double", label: "Double" },
  { value: "triple", label: "Triple" },
  { value: "quad", label: "Quad" },
];

export default function StaahRoomMapping({ isResavenue }) {
  const {
    roomsData,
    changeHandler,
    handleSaveRooms,
    isSaveForRoomDisabled,
    isLoading,
    loaderRoomIndex,
    handleDragEnd,
    isFetching,
    addRoom,
    snackbarMsg,
    setSnackBarMsg,
    fetchRooms,
  } = useStaahRooms(isResavenue);

  const { permission, authFetch, hotelSelect } = useAuth();
  const [confirmationDialog, setConfirmationDialog] = useState(null);

  const hasDeleteRoomRatePlanPermission = permission?.some(
    (p) => p.name === "deleteRatePlan"
  );

  const handleDeleteRoomRatePlan = async (roomRatePlanId) => {
    if (!hotelSelect?.id || !roomRatePlanId) return;

    try {
      const { del } = await authFetch({
        path: `/hotel/${hotelSelect.id}/room-rate-plan/${roomRatePlanId}`,
      });

      const { response } = await del();

      if (response.ok) {
        if (fetchRooms) {
          fetchRooms();
        }
      }
    } catch (err) {
      console.error("Error deleting room rate plan:", err);
      setSnackBarMsg({
        open: true,
        msg: "An error occurred while deleting. Please try again.",
        color: "error",
      });
    }
  };

  if (isFetching) {
    return (
      <Stack alignItems="center" justifyContent="center" p={4} height={"50vh"}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack gap="24px" backgroundColor="#ffffff" paddingX={3} paddingY={4}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography className="pageHeading">
          Room Mapping for Channel Manager
        </Typography>
      </Stack>

      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={roomsData.map((room) => room.uid)}
          strategy={verticalListSortingStrategy}
        >
          {roomsData.map((room, index) => (
            <SortableItem key={room.uid} id={room.uid}>
              <Stack
                gap="16px"
                direction="row"
                alignItems="center"
                padding="24px"
                backgroundColor="#ffffff"
                boxShadow={"1px 4px 10px rgba(48, 111, 188, 0.2)"}
              >
                <TextField
                  label="Sequence"
                  type="number"
                  value={room.sequence}
                  InputProps={{ readOnly: true }}
                  sx={{ width: "6%" }}
                />
                <TextField
                  label="Room ID"
                  type="number"
                  value={room?.roomId || ""}
                  onChange={(e) =>
                    changeHandler(index, "roomId", e.target.value)
                  }
                />
                <TextField
                  label="Room Name"
                  value={room?.roomName || ""}
                  onChange={(e) =>
                    changeHandler(index, "roomName", e.target.value)
                  }
                />
                <TextField
                  label="Rate Plan ID"
                  value={room?.roomRateGroupCode || ""}
                  onChange={(e) =>
                    changeHandler(index, "roomRateGroupCode", e.target.value)
                  }
                />
                {isResavenue && (
                  <TextField
                    label="Rate Plan Name"
                    value={room?.roomRatePlanName || ""}
                    onChange={(e) =>
                      changeHandler(index, "roomRatePlanName", e.target.value)
                    }
                  />
                )}
                <TextField
                  label={`${isResavenue ? "Promotional Markup (%)" : "Promo Discount (%)"}`}
                  type="number"
                  value={room?.promotionalDiscount || ""}
                  sx={{ width: "10%" }}
                  onChange={(e) =>
                    changeHandler(index, "promotionalDiscount", e.target.value)
                  }
                />
                {isResavenue && (
                  <Autocomplete
                    value={
                      roomTypes.find(
                        (rt) => rt.value === room?.occupancyType
                      ) || null
                    }
                    onChange={(_, newValue) => {
                      changeHandler(
                        index,
                        "occupancyType",
                        newValue ? newValue?.value : null
                      );
                    }}
                    options={roomTypes}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    renderInput={(params) => (
                      <TextField {...params} label="Occupancy Type" />
                    )}
                    sx={{ minWidth: 200 }}
                  />
                )}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={room?.isBaseRoom || false}
                      onChange={() =>
                        changeHandler(index, "isBaseRoom", !room?.isBaseRoom)
                      }
                    />
                  }
                  label={
                    isResavenue ? "Is Base Room Rate Plan" : "Is Base Room"
                  }
                />
                <LoadingButton
                  variant="contained"
                  disabled={isSaveForRoomDisabled(index)}
                  loading={isLoading && loaderRoomIndex === index}
                  onClick={() => handleSaveRooms(index, true)}
                >
                  Save
                </LoadingButton>
                {hasDeleteRoomRatePlanPermission && (
                  <Tooltip title="Delete Room Rate Plan">
                    <IconButton
                      onClick={() =>
                        setConfirmationDialog(room?.RoomRatePlanId)
                      }
                    >
                      <DeleteIcon sx={{ color: '#306FBC' }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </SortableItem>
          ))}
        </SortableContext>
      </DndContext>

      <Stack flexDirection={"row"} justifyContent={"flex-end"}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={addRoom}>
          Add Room
        </Button>
      </Stack>

      <Dialog
        open={!!confirmationDialog}
        onClose={() => setConfirmationDialog(null)}
      >
        <DialogTitle>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ marginTop: "20px" }}
          >
            <DialogLabel style={{ textAlign: "center" }}>
              Are you sure you want to delete this room rate plan?
            </DialogLabel>
            <Cross
              style={{ position: "absolute", top: "10px", right: "10px" }}
              onClick={() => setConfirmationDialog(null)}
            />
          </Stack>
        </DialogTitle>
        <DialogActions
          style={{
            justifyContent: "center",
            gap: "16px",
            paddingBottom: 16,
            marginBottom: "20px",
          }}
        >
          <PrimaryButton
            autoFocus
            next
            onClick={() => {
              handleDeleteRoomRatePlan(confirmationDialog);
              setConfirmationDialog(null);
            }}
          >
            Yes
          </PrimaryButton>
          <Button
            variant="outlined"
            style={{ height: "32px" }}
            onClick={() => {
              setConfirmationDialog(null);
            }}
          >
            No
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={snackbarMsg.open}
        autoHideDuration={6000}
        onClose={() => {
          setSnackBarMsg({
            ...snackbarMsg,
            open: false,
          });
        }}
      >
        <SnackbarContent
          sx={{
            backgroundColor: snackbarMsg.color,
            textTransform: "capitalize",
          }}
          message={snackbarMsg.msg}
        />
      </Snackbar>
    </Stack>
  );
}

const Cross = styled(CloseRounded)`
  cursor: pointer;
  font-size: 24px;
  color: #666;
  &:hover {
    color: #000;
  }
`;

function SortableItem({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: "grab",
    background: isDragging ? "#f5f5f5" : "transparent",
    borderRadius: "8px",
  };

  return (
    <Stack
      ref={setNodeRef}
      style={style}
      direction="row"
      alignItems="center"
      gap={1}
    >
      <DragIndicatorIcon
        {...attributes}
        {...listeners}
        sx={{ cursor: "grab", color: "gray" }}
      />
      <Stack flex={1}>{children}</Stack>
    </Stack>
  );
}
